/**
 * Settings ⇄ database sync layer.
 *
 * The Settings pages drive the shared `@intrasync/stores` `SettingsStore`, which
 * is intentionally closed: every setter persists ONLY to localStorage (see
 * `node_modules/@intrasync/stores/dist/specialized/SettingsStore.js`). That keeps
 * a setting from following the user to another device or browser.
 *
 * This module bridges that gap WITHOUT forking the library store. It:
 *   1. Hydrates the live store from the `user-preferences` service (`GET .../me`)
 *      the moment a user becomes authenticated, applying DB values through the
 *      store's own public setters (which also refreshes the localStorage cache).
 *   2. Watches the synced slice of the store and debounce-PATCHes
 *      `user-preferences/me` whenever the user changes anything — so localStorage
 *      becomes a fast cache in front of the database, not the system of record.
 *
 * Field mapping (store → DB record, see `UserPreferencesRecord` on the API):
 *   - typed columns: theme, timezone, dateFormat, emailNotifications,
 *     browserNotifications (← desktop notifications), plus dashboardLayout +
 *     notificationPreferences JSON blobs.
 *   - everything else (language, fontSize, timeFormat, currency, sms, sidebar,
 *     table density/page size) rides in the free-form `preferences` JSON column.
 * OmniBar Favorites ride in `preferences.favorites` (route keys) through the
 * SAME snapshot as every other key: the API replaces the whole `preferences`
 * blob on PATCH, so a second writer would be wiped by the next theme change.
 * `landingRoute` is left untouched — the Settings UI doesn't expose it, so we
 * never want to clobber a value set elsewhere.
 */
import { reaction, runInAction } from "mobx";
import type { SettingsStore } from "@intrasync/stores";
import type { ServiceStoreType } from "../types/ServiceStoreTypes";
import { HQ_DEFAULT_FAVORITES, asFavorites } from "../config/favorites";

/** The singleton id the `user-preferences` service resolves to the caller's row. */
const PREFS_ME = "me";
/** Wait this long after the last change before writing, to coalesce bursts. */
const SAVE_DEBOUNCE_MS = 600;
/** Re-read the row when the tab comes back, at most this often. */
const REHYDRATE_MIN_MS = 30_000;

/** The `user-preferences` record/patch shape we read and write (subset). */
interface PreferencesPayload {
  theme?: string;
  timezone?: string | null;
  dateFormat?: string;
  emailNotifications?: boolean;
  browserNotifications?: boolean;
  notificationPreferences?: Record<string, unknown> | null;
  preferences?: Record<string, unknown> | null;
  dashboardLayout?: Record<string, unknown> | null;
}

/** A Feathers NotAuthenticated rejection (401), by name or code. */
function isNotAuthenticated(err: unknown): boolean {
  const e = err as { name?: unknown; code?: unknown } | null;
  return !!e && (e.name === "NotAuthenticated" || e.code === 401);
}

/** Minimal structural view of the root store this layer needs. */
interface SyncCapableRoot {
  settingsStore?: SettingsStore;
  authStore?: {
    currentUser?: { id?: number } | null;
    /**
     * The library AuthStore's "session established" flag — flipped true only
     * once Feathers has actually authenticated the transport. `currentUser.id`
     * alone is NOT that signal: the AuthStore restores the remembered user from
     * localStorage at boot, so an id exists on the sign-in page too.
     */
    isSignedIn?: boolean;
  };
  /**
   * The library AppLayoutStore: its pinned pages ARE the OmniBar's Favorites.
   * Optional so fakes without it keep working; `setPinnedPages` arrived in
   * @intrasync/stores 31.4.0.
   */
  appLayoutStore?: {
    pinnedPageKeys: string[];
    setPinnedPages?: (keys: readonly string[], opts?: { persist?: boolean }) => void;
  } | null;
  /** The registered `user-preferences` ServiceStore (system.stores.ts). */
  userPreferencesStore?: ServiceStoreType;
}

const THEMES = new Set(["light", "dark", "auto"]);
const DATE_FORMATS = new Set(["MM/dd/yyyy", "dd/MM/yyyy", "yyyy-MM-dd"]);
const FONT_SIZES = new Set(["small", "medium", "large"]);
const LAYOUTS = new Set(["grid", "list"]);
const DENSITIES = new Set(["compact", "standard", "comfortable"]);

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

/**
 * Push a DB preferences record into the live store via its public setters.
 * Each branch is guarded so a stale/foreign value (e.g. the server's default
 * `MM/DD/YYYY`, which isn't one of the UI's date-fns options) is ignored rather
 * than left dangling in a Select. Caller wraps this in a single action so the
 * save reaction sees one batched change.
 */
function applyRecordToStore(store: SettingsStore, rec: PreferencesPayload): void {
  const { userSettings, deviceSettings } = store;
  const prefs = asObject(rec.preferences);
  const notif = asObject(rec.notificationPreferences);
  const dash = asObject(rec.dashboardLayout);

  // ---- typed columns ----
  if (typeof rec.theme === "string" && THEMES.has(rec.theme)) {
    deviceSettings.setTheme(rec.theme as "light" | "dark" | "auto");
  }
  if (typeof rec.timezone === "string" && rec.timezone) {
    userSettings.setTimeZone(rec.timezone);
  }
  if (typeof rec.dateFormat === "string" && DATE_FORMATS.has(rec.dateFormat)) {
    userSettings.setDateFormat(rec.dateFormat);
  }
  userSettings.updateNotificationSettings({
    EmailNotifications: !!rec.emailNotifications,
    ...(typeof prefs.smsNotifications === "boolean"
      ? { SmsNotifications: prefs.smsNotifications }
      : {}),
  });
  deviceSettings.updateNotifications({
    desktop: !!rec.browserNotifications,
    ...(typeof notif.enabled === "boolean" ? { enabled: notif.enabled } : {}),
    ...(typeof notif.sound === "boolean" ? { sound: notif.sound } : {}),
  });

  // ---- free-form `preferences` blob ----
  if (typeof prefs.language === "string") deviceSettings.setLanguage(prefs.language);
  if (typeof prefs.fontSize === "string" && FONT_SIZES.has(prefs.fontSize)) {
    deviceSettings.setFontSize(prefs.fontSize as "small" | "medium" | "large");
  }
  if (prefs.timeFormat === "12h" || prefs.timeFormat === "24h") {
    userSettings.setTimeFormat(prefs.timeFormat);
  }
  if (typeof prefs.currency === "string") userSettings.setCurrency(prefs.currency);
  const table = asObject(prefs.table);
  const tableUpdate: { pageSize?: number; density?: "compact" | "standard" | "comfortable" } = {};
  if (typeof table.pageSize === "number") tableUpdate.pageSize = table.pageSize;
  if (typeof table.density === "string" && DENSITIES.has(table.density)) {
    tableUpdate.density = table.density as "compact" | "standard" | "comfortable";
  }
  if (Object.keys(tableUpdate).length) deviceSettings.updateTable(tableUpdate);

  // ---- dashboard layout blob ----
  const dashUpdate: {
    layout?: "grid" | "list";
    autoRefresh?: boolean;
    refreshInterval?: number;
  } = {};
  if (typeof dash.layout === "string" && LAYOUTS.has(dash.layout)) {
    dashUpdate.layout = dash.layout as "grid" | "list";
  }
  if (typeof dash.autoRefresh === "boolean") dashUpdate.autoRefresh = dash.autoRefresh;
  if (typeof dash.refreshInterval === "number") dashUpdate.refreshInterval = dash.refreshInterval;
  if (Object.keys(dashUpdate).length) deviceSettings.updateDashboard(dashUpdate);
}

/**
 * Apply the synced Favorites. A present `favorites` list, even an empty one, is
 * the user's own and always wins. With no key yet (first sign-in after this
 * shipped), keep what this browser already holds for the user, else seed HQ's
 * defaults; the save that follows writes the key.
 */
function applyFavorites(root: SyncCapableRoot, rec: PreferencesPayload | undefined): void {
  const layout = root.appLayoutStore;
  if (!layout?.setPinnedPages) return;
  const synced = asFavorites(asObject(rec?.preferences).favorites);
  if (synced) {
    layout.setPinnedPages(synced);
  } else if (layout.pinnedPageKeys.length === 0) {
    layout.setPinnedPages(HQ_DEFAULT_FAVORITES);
  }
}

/**
 * Read the synced slice of the store into a plain, serialisable payload that is
 * BOTH the change-detection key and the PATCH body. Every field is dereferenced
 * here so the enclosing reaction tracks them all.
 */
function snapshotStore(store: SettingsStore, root: SyncCapableRoot): PreferencesPayload {
  const { userSettings, deviceSettings } = store;
  const notif = deviceSettings.notifications;
  const dashboard = deviceSettings.dashboard;
  const table = deviceSettings.table;
  return {
    theme: deviceSettings.theme,
    timezone: userSettings.timeZone,
    dateFormat: userSettings.dateFormat,
    emailNotifications: userSettings.emailNotifications,
    browserNotifications: notif.desktop,
    notificationPreferences: {
      enabled: notif.enabled,
      sound: notif.sound,
      desktop: notif.desktop,
      email: userSettings.emailNotifications,
      sms: userSettings.smsNotifications,
    },
    dashboardLayout: {
      layout: dashboard.layout,
      autoRefresh: dashboard.autoRefresh,
      refreshInterval: dashboard.refreshInterval,
    },
    preferences: {
      language: deviceSettings.language,
      fontSize: deviceSettings.fontSize,
      timeFormat: userSettings.timeFormat,
      currency: userSettings.defaultCurrency,
      smsNotifications: userSettings.smsNotifications,
      sidebarCollapsed: deviceSettings.sidebarCollapsed,
      table: { pageSize: table.pageSize, density: table.density },
      ...(root.appLayoutStore?.setPinnedPages
        ? { favorites: [...root.appLayoutStore.pinnedPageKeys] }
        : {}),
    },
  };
}

/**
 * Wire the live `settingsStore` to the `user-preferences` database service.
 * Safe to call once during root-store construction; no-ops if the required
 * stores are absent (e.g. test harness). Returns a disposer.
 */
export function installSettingsSync(root: SyncCapableRoot): () => void {
  // Deref the stores LAZILY (inside the reactions) rather than capturing them
  // here, so this is order-independent: it can be installed from an app-store
  // (appStoresConfig.ts) regardless of whether `settingsStore`/`authStore`/the
  // `user-preferences` ServiceStore are mounted on `root` yet — the reactions
  // resolve them when they fire.
  const prefsService = (): ServiceStoreType | null =>
    root.userPreferencesStore ?? null;
  // The user id we've finished a hydrate attempt for. Saves are gated on this
  // matching the current user, which (a) suppresses the echo from hydration's
  // own setters and (b) ignores any change made before the load returns — that
  // change would be clobbered by the incoming DB values anyway.
  let hydratedFor: number | null = null;
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let lastSaved: string | null = null;
  let lastHydratedAt = 0;
  /** True while a hydrate applies server values: those must not echo back as a save. */
  let applyingServer = false;

  /**
   * The user whose preferences may be read/written RIGHT NOW: the current
   * user's id, but only while the session is established (`isSignedIn`).
   *
   * Gating on the id alone is what put an orange "Not authenticated" toast
   * over the sign-in form: the AuthStore restores the remembered user from
   * localStorage before any authentication happens, so `GET .../me` went out
   * on an unauthenticated socket, the API's app-level guard answered
   * NotAuthenticated, and the library ServiceStore's error reaction
   * (`errorOnGet` → `errorHandler` → `handleNotAuthenticated`) turned that into
   * a warning notification. Waiting for `isSignedIn` means the request is
   * never issued while signing in is still the expected state.
   */
  const signedInUserId = (): number | null => {
    const auth = root.authStore;
    if (!auth || auth.isSignedIn !== true) return null;
    return auth.currentUser?.id ?? null;
  };

  // Only arm saves if that same user is still signed in when the load settles —
  // a response landing after sign-out must not mark the NEXT session hydrated.
  const markHydrated = (userId: number): void => {
    if (signedInUserId() === userId) hydratedFor = userId;
  };

  /** Debounce-PATCH a snapshot (JSON from `snapshotStore`). */
  const scheduleSave = (snapshotJson: string): void => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = null;
      lastSaved = snapshotJson;
      const body = JSON.parse(snapshotJson) as PreferencesPayload;
      // PATCH .../me upserts the authenticated user's preferences row.
      Promise.resolve(prefsService()?.patch(PREFS_ME, body as never)).catch((err) => {
        console.warn("[settingsSync] failed to persist user-preferences:", err);
      });
    }, SAVE_DEBOUNCE_MS);
  };

  const hydrate = async (userId: number): Promise<void> => {
    const store = root.settingsStore;
    if (!store) return;
    try {
      // GET .../me resolves the authenticated user's preferences row.
      const rec = (await prefsService()?.get(PREFS_ME)) as
        | PreferencesPayload
        | undefined;
      applyingServer = true;
      try {
        runInAction(() => {
          if (rec) applyRecordToStore(store, rec);
          applyFavorites(root, rec);
        });
      } finally {
        applyingServer = false;
      }
      // Seed the de-dupe key so the post-hydrate snapshot (now equal to the DB)
      // doesn't trigger a redundant write-back.
      const snapshot = JSON.stringify(snapshotStore(store, root));
      lastSaved = snapshot;
      lastHydratedAt = Date.now();
      markHydrated(userId);
      // First sign-in since Favorites shipped: the row has no `favorites` key,
      // so write the list just applied (the user's own or HQ's defaults) now,
      // rather than on whatever setting they happen to change next.
      if (
        root.appLayoutStore?.setPinnedPages &&
        asFavorites(asObject(rec?.preferences).favorites) === null &&
        hydratedFor === userId
      ) {
        scheduleSave(snapshot);
      }
    } catch (err) {
      if (isNotAuthenticated(err)) {
        // The session lapsed between the sign-in flip and the response. That
        // is the auth layer's to handle (refresh or reroute), not ours to
        // report: stay quiet and leave saves OFF, so nothing is PATCHed into a
        // refused session. The next sign-in flip re-hydrates.
        return;
      }
      // Leave localStorage/defaults in place; persistence still turns on so
      // the next change is written (PATCH .../me upserts a row).
      console.warn("[settingsSync] failed to load user-preferences:", err);
      markHydrated(userId);
    }
  };

  const disposeHydrate = reaction(
    signedInUserId,
    (userId) => {
      if (userId && userId !== hydratedFor) {
        void hydrate(userId);
      } else if (!userId) {
        // Signed out: drop any write still waiting on the debounce so it is
        // not sent on a session that no longer exists.
        hydratedFor = null;
        lastSaved = null;
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = null;
      }
    },
    { fireImmediately: true }
  );

  const disposeSave = reaction(
    () => {
      const store = root.settingsStore;
      return store ? JSON.stringify(snapshotStore(store, root)) : "";
    },
    (snapshotJson) => {
      if (!snapshotJson) return; // settingsStore not mounted yet
      const userId = signedInUserId();
      if (!userId || userId !== hydratedFor) return; // signed out / mid-hydrate
      if (applyingServer) return; // hydrate's own setters (a re-hydrate)
      if (snapshotJson === lastSaved) return; // nothing actually changed
      scheduleSave(snapshotJson);
    }
  );

  // Favorites (and every other setting) may have changed on another device.
  // The PATCH replaces the whole blob, so a stale tab that saves would undo
  // them: re-read the row when the tab becomes visible or the device comes back
  // online, but never over an unsaved local change.
  const rehydrateIfClean = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    const userId = signedInUserId();
    const store = root.settingsStore;
    if (!userId || userId !== hydratedFor || !store) return;
    if (saveTimer) return; // a save is pending
    if (JSON.stringify(snapshotStore(store, root)) !== lastSaved) return; // dirty
    if (Date.now() - lastHydratedAt < REHYDRATE_MIN_MS) return;
    void hydrate(userId);
  };
  const hasWindow = typeof window !== "undefined" && typeof document !== "undefined";
  if (hasWindow) {
    document.addEventListener("visibilitychange", rehydrateIfClean);
    window.addEventListener("online", rehydrateIfClean);
  }

  return () => {
    if (hasWindow) {
      document.removeEventListener("visibilitychange", rehydrateIfClean);
      window.removeEventListener("online", rehydrateIfClean);
    }
    disposeHydrate();
    disposeSave();
    if (saveTimer) clearTimeout(saveTimer);
  };
}
